import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AbstractStateInterface } from '../../interface/abstract';
import { LearningInterface } from '../../interface/learning';
import { ApiService } from '../../utils/apiService';
import { InterfaceToSearchParams } from '../../utils/helper';

type DataState = AbstractStateInterface<LearningInterface>;

export interface LearningSearch {
  id?: string;
}

const initialState: DataState = {
  data: [],
  detail: null,
  loading: false,
};

export const findLearningByCondition = createAsyncThunk(
  'admin-api/learning/get',
  async (payload: LearningSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/learning?${searchParams}`, 'GET', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const getAllLearnings = createAsyncThunk('admin-api/learning/all', async (_, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/learning/all`, 'GET', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const createLearning = createAsyncThunk('admin-api/learning', async (payload: FormData, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/learning`, 'POST', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const updateLearning = createAsyncThunk('admin-api/learning/put', async (payload: FormData, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/learning`, 'PUT', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const deleteLearning = createAsyncThunk(
  'admin-api/learning/delete',
  async (payload: LearningSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/learning?${searchParams}`, 'DELETE', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

const learningSlice = createSlice({
  name: 'learning',
  initialState,
  reducers: {
    resetLearningSlice: () => {
      return { ...initialState };
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllLearnings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllLearnings.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.data = action.payload.data as LearningInterface[];
        } else {
          state.data = [];
        }
      })
      .addCase(getAllLearnings.rejected, (state) => {
        state.loading = false;
      })
      .addCase(findLearningByCondition.pending, (state) => {
        state.loading = true;
      })
      .addCase(findLearningByCondition.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.detail = action.payload.data[0] as LearningInterface;
        } else {
          state.detail = null;
        }
      })
      .addCase(findLearningByCondition.rejected, (state) => {
        state.loading = false;
      });
  },
});
export const { resetLearningSlice, resetDetail } = learningSlice.actions;
export default learningSlice.reducer;
